import { Switch, Route, useLocation } from "wouter";
import { browserStores, captureTrialLink } from "@/lib/trialDeepLink";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/hooks/use-theme";
import { OrganizationProvider, useOrganization } from "@/contexts/OrganizationContext";
import { NotificationsProvider } from "@/contexts/NotificationsContext";
import { GlobalVoiceProvider } from "@/components/voice/GlobalVoiceProvider";
import { GlobalDialerProvider } from "@/contexts/GlobalDialerContext";
import GlobalCallButton from "@/components/voice/GlobalCallButton";
import { MilestoneCelebrationProvider } from "@/components/milestones/MilestoneCelebrationProvider";
import { UpgradeModalProvider } from "@/hooks/useUpgradeModal";
import { GlobalUpgradeModal } from "@/components/billing/GlobalUpgradeModal";
import { DirectUpgradeProvider } from "@/components/billing/DirectUpgradeSheet";
import { useAuth } from "@/hooks/useAuth";
import { useDeviceDetection } from "@/hooks/useDeviceDetection";
import { useCapabilities } from "@/hooks/useCapabilities";
import { usePageTracking } from "@/hooks/usePageTracking";
import { useHubspotChat } from "@/hooks/useHubspotChat";
import { usePublicOrderChat } from "@/hooks/usePublicOrderChat";

/** Loads the HubSpot chat, anonymously, on the logged-out order pages. */
function PublicOrderChat() {
  const [location] = useLocation();
  usePublicOrderChat(location);
  return null;
}
import { ConditionalAnalytics } from "@/components/analytics/ConditionalAnalytics";
import FreePlanBanner from "@/components/trial/FreePlanBanner";
import { PaymentFailureBanner } from "@/components/billing/PaymentFailureBanner";
import { UsageLimitWarnings } from "@/components/limits/UsageLimitWarnings";
import { GlobalLimitHandler } from "@/components/limits/GlobalLimitHandler";
import { DashboardRightSidebar } from "@/components/dashboard/DashboardRightSidebar";
import { ProductTourProvider, WelcomeTourModal, TourTrigger } from "@/components/tour";
import { useEffect, lazy, Suspense } from "react";
import { isLimecallHost } from "@/lib/brand";

/**
 * Retry dynamic imports that fail due to stale chunk references after deploys.
 * On failure, clears module cache and retries once. If that also fails, reloads the page
 * so the browser fetches the new HTML with updated asset hashes.
 */
function lazyRetry<T extends { default: any }>(
  importFn: () => Promise<T>,
): React.LazyExoticComponent<T['default']> {
  return lazy(() =>
    importFn().catch(() => {
      // First retry — the import URL may have been cached
      return importFn().catch(() => {
        // If we haven't already reloaded for this deploy, do a hard reload
        const reloadKey = 'chunk-reload-' + window.location.pathname;
        if (!sessionStorage.getItem(reloadKey)) {
          sessionStorage.setItem(reloadKey, '1');
          window.location.reload();
        }
        // Return a no-op module to satisfy the type system while reloading
        return { default: (() => null) as any } as T;
      });
    }),
  );
}

// Lightweight pages kept eager (small bundle cost, on critical auth path)
import Login from "@/pages/auth/login";
import Register from "@/pages/auth/register";
import NotFound from "@/pages/not-found";
import { KeyboardShortcuts } from "@/components/layout/KeyboardShortcuts";

// Previously eager pages now lazy-loaded for faster initial load
const Welcome = lazyRetry(() => import("@/pages/welcome"));
const BillingCredits = lazyRetry(() => import("@/pages/BillingCredits"));
const FreeCredits = lazyRetry(() => import("@/pages/FreeCredits"));
const PersonalDashboard = lazyRetry(() => import("@/pages/PersonalDashboard"));
const LimeCallDashboard = lazyRetry(() => import("@/pages/limecall/LimeCallDashboard"));
const DemoRequest = lazyRetry(() => import("@/pages/DemoRequest"));
const DemoLanding = lazyRetry(() => import("@/pages/DemoLanding"));
const InboxConversations = lazyRetry(() => import("@/pages/inbox-conversations"));
const Landing = lazyRetry(() => import("@/pages/landing-number-search"));
const GoogleCallback = lazyRetry(() => import("@/pages/auth/google-callback"));

// Lazy-loaded pages (secondary flows - loaded on demand)
const DashboardSelector = lazyRetry(() => import("@/pages/DashboardSelector"));
const CompanyDashboard = lazyRetry(() => import("@/pages/CompanyDashboard"));
const InboxRedesigned = lazyRetry(() => import("@/pages/_legacy-inbox-redesigned"));
const Inbox = lazyRetry(() => import("@/pages/_legacy-inbox"));
const TextInbox = lazyRetry(() => import("@/pages/TextInbox"));
const EnhancedVoicePage = lazyRetry(() => import("@/pages/voice-enhanced"));
const VoiceDialerPage = lazyRetry(() => import("@/pages/voice-dialer"));
const PowerDialerPage = lazyRetry(() => import("@/pages/power-dialer"));
const VoiceCalls = lazyRetry(() => import("@/pages/voice-calls"));
const CallRecords = lazyRetry(() => import("@/pages/call-records"));
const Voicemails = lazyRetry(() => import("@/pages/voicemails"));
const ContactsRedesigned = lazyRetry(() => import("@/pages/contacts-redesigned"));
const Customers = lazyRetry(() => import("@/pages/Customers"));
const PhoneNumbers = lazyRetry(() => import("@/pages/phone-numbers-new"));
const PhoneManagementImproved = lazyRetry(() => import("@/pages/phone-management-improved"));
const Schedule = lazyRetry(() => import("@/pages/schedule"));
const Automations = lazyRetry(() => import("@/pages/automations"));
const FollowUps = lazyRetry(() => import("@/pages/follow-ups"));
const Teams = lazyRetry(() => import("@/pages/teams"));
const TeamChat = lazyRetry(() => import("@/pages/team-chat"));
const GetStarted = lazyRetry(() => import("@/pages/get-started"));
const SearchPage = lazyRetry(() => import("@/pages/search"));
const Helpdesk = lazyRetry(() => import("@/pages/helpdesk"));
const Activation = lazyRetry(() => import("@/pages/auth/activation"));
const VerifyEmail = lazyRetry(() => import("@/pages/auth/verify-email"));
const ForgotPassword = lazyRetry(() => import("@/pages/auth/forgot-password"));
const ResetPassword = lazyRetry(() => import("@/pages/auth/reset-password"));
const Setup = lazyRetry(() => import("@/pages/setup"));
const GettingStarted = lazyRetry(() => import("@/pages/onboarding/GettingStarted"));
const ReactivationPage = lazyRetry(() => import("@/pages/ReactivationPage"));
const SmsSetup = lazyRetry(() => import("@/pages/sms-setup"));
const MobileLogin = lazyRetry(() => import("@/pages/mobile/login"));

// Lazy-loaded pages (heavy/infrequently accessed)
const AdminApp = lazyRetry(() => import("@/pages/admin/app"));
const Analytics = lazyRetry(() => import("@/pages/analytics"));
const TestAnalytics = lazyRetry(() => import("@/pages/TestAnalytics"));
const ComplianceDashboard = lazyRetry(() => import("@/pages/compliance-enhanced"));
const Enhanced10DLCRegistration = lazyRetry(() => import("@/pages/10DLC-Registration-Enhanced"));
const TrustCenter = lazyRetry(() => import("@/pages/trust-center"));
const KYCStatusPage = lazyRetry(() => import("@/pages/kyc-status"));
const VerifyNumberPage = lazyRetry(() => import("@/pages/verify-number"));
const NumberKycPage = lazyRetry(() => import("@/pages/numberkyc"));
const KycUploadPage = lazyRetry(() => import("@/pages/kyc-upload"));
const KycPortalPage = lazyRetry(() => import("@/pages/kyc-portal"));
const IntakeLinkPage = lazyRetry(() => import("@/pages/intake-link"));
const ClickToCallPage = lazyRetry(() => import("@/pages/click-to-call"));
const KycLinkExpiredPage = lazyRetry(() => import("@/pages/kyc-link-expired"));
const StatusPage = lazyRetry(() => import("@/pages/status"));
const OnboardingPage = lazyRetry(() => import("@/pages/onboarding"));
const VoiceAgents = lazyRetry(() => import("@/pages/voice-agents"));
const VoiceAgentNew = lazyRetry(() => import("@/pages/voice-agent-new"));
const InboundRouting = lazyRetry(() => import("@/pages/inbound-routing"));
const QueueManagement = lazyRetry(() => import("@/pages/QueueManagement"));
const Campaigns = lazyRetry(() => import("@/pages/campaigns-redesigned"));
const CampaignsLegacy = lazyRetry(() => import("@/pages/campaigns"));
const Templates = lazyRetry(() => import("@/pages/templates"));
const Workflows = lazyRetry(() => import("@/pages/workflows"));
const MobileApp = lazyRetry(() => import("@/pages/mobile/index").then(m => ({ default: m.MobileApp })));
const MobileInboxLegacy = lazyRetry(() => import("@/pages/mobile/index").then(m => ({ default: m.default })));
const SettingsNew = lazyRetry(() => import("@/pages/settings-new"));
const SlackLinkPage = lazyRetry(() => import("@/pages/slack-link"));
const PaymentsSettings = lazyRetry(() => import("@/pages/payments-settings"));
const AssistantPage = lazyRetry(() => import("@/pages/assistant"));
const HelpCenter = lazyRetry(() => import("@/pages/HelpCenter").then(m => ({ default: m.HelpCenter })));
const ApiDocs = lazyRetry(() => import("@/pages/api-docs"));
const PricingPage = lazyRetry(() => import("@/pages/PricingPage").then(m => ({ default: m.PricingPage })));
const Plans = lazyRetry(() => import("@/pages/Plans"));
const RatesPage = lazyRetry(() => import("@/pages/RatesPage"));
const FAQPage = lazyRetry(() => import("@/pages/FAQPage"));
const ContactSalesPage = lazyRetry(() => import("@/pages/ContactSalesPage"));
const WebDialerDemo = lazyRetry(() => import("@/pages/WebDialerDemo"));
const StartFromMarketingPage = lazyRetry(() => import("@/pages/order/StartFromMarketingPage"));
const SelectPlanPage = lazyRetry(() => import("@/pages/order/SelectPlanPage"));
const KycAddressPage = lazyRetry(() => import("@/pages/order/KycAddressPage"));
const AccountSuspendedPage = lazyRetry(() => import("@/pages/account-suspended"));
const PendingApprovalPage = lazyRetry(() => import("@/pages/pending-approval"));
const CheckoutPage = lazyRetry(() => import("@/pages/order/CheckoutPage"));
const CheckoutSuccessPage = lazyRetry(() => import("@/pages/order/CheckoutSuccessPage"));
const CheckoutClaimPage = lazyRetry(() => import("@/pages/order/CheckoutClaimPage"));
const PostCheckoutPage = lazyRetry(() => import("@/pages/order/PostCheckoutPage"));

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
  </div>
);

const SentryFallback = ({ error, resetError }: { error: Error; resetError: () => void }) => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="max-w-md mx-auto text-center p-8">
      <h2 className="text-2xl font-bold text-foreground mb-4">Something went wrong</h2>
      <p className="text-muted-foreground mb-6">
        An unexpected error occurred. Our team has been notified and is looking into it.
      </p>
      <button
        onClick={() => { resetError(); window.location.href = '/'; }}
        className="px-6 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
      >
        Return to Dashboard
      </button>
    </div>
  </div>
);

function Router() {
  const { isAuthenticated, isLoading } = useAuth();
  const [location, setLocation] = useLocation();
  const { currentOrganization } = useOrganization();
  
  // Check if user is on onboarding page (robust check for nested routes)
  const isOnboardingPage = location.startsWith('/onboarding');
  
  // Check if organization is awaiting approval
  const isAwaitingApproval = currentOrganization?.status === 'awaiting_approval';
  
  // Check if organization has a paid plan (not trial/free)
  const hasPaidPlan = currentOrganization?.planType && 
    !['trial', 'free'].includes(currentOrganization.planType.toLowerCase());
  
  // Check if organization is cancelled/restricted and needs reactivation
  // Don't force reactivation if they have a paid plan (lifecycle may be stale)
  const needsReactivation = isAuthenticated && currentOrganization && !hasPaidPlan && (
    currentOrganization.lifecycle === 'canceled' || 
    (currentOrganization.accessPosture === 'restricted' && currentOrganization.lifecycle === 'trialing')
  );
  
  // Redirect cancelled orgs to reactivation page (except if already there) - use effect to avoid render loop
  useEffect(() => {
    // /kyc/ is excluded deliberately: it is a public, token-scoped page opened
    // from an email, and the customer holding that link very often IS on a
    // canceled or restricted org — bouncing them to /reactivate is exactly the
    // wall this page exists to remove.
    if (needsReactivation && !location.startsWith('/reactivate') && !location.startsWith('/plans')
        && !location.startsWith('/kyc/') && !location.startsWith('/intake/')) {
      setLocation('/reactivate');
    }
  }, [needsReactivation, location, setLocation]);

  // Number-order document upload, opened from a one-off link we email. Checked
  // BEFORE the auth gates and before the loading spinner: the token authorises
  // one order and carries no session, so there is nothing to wait for and
  // nothing to sign in to. Without this route the link 404s into the landing
  // page — which is what it did for every LimePhone-brand order, the same
  // failure the /kyc-upload link had before it.
  {
    const kycPath = window.location.pathname;
    if (kycPath.startsWith('/kyc/')) {
      const token = decodeURIComponent(kycPath.slice('/kyc/'.length).split(/[/?#]/)[0] || '');
      if (token) {
        return (
          <Suspense fallback={<PageLoader />}>
            <KycPortalPage token={token} />
          </Suspense>
        );
      }
    }
  }

  // The activation intake form, opened from the one-time link in our "one step
  // left to activate" email. Same placement and same reasons as /kyc/ above:
  // the token opens one form on one account and carries no session, and the
  // person holding it is by definition an account stuck at review — which is
  // exactly who every gate below would send somewhere else.
  {
    const intakePath = window.location.pathname;
    if (intakePath.startsWith('/intake/')) {
      const token = decodeURIComponent(intakePath.slice('/intake/'.length).split(/[/?#]/)[0] || '');
      if (token) {
        return (
          <Suspense fallback={<PageLoader />}>
            <IntakeLinkPage token={token} />
          </Suspense>
        );
      }
    }
  }

  // Public click-to-call page (/c/:slug): anonymous visitors, signed in or
  // not, so it sits ahead of every auth/activation gate like /intake/.
  {
    const c2cPath = window.location.pathname;
    if (c2cPath.startsWith('/c/')) {
      const slug = decodeURIComponent(c2cPath.slice('/c/'.length).split(/[/?#]/)[0] || '');
      if (slug) {
        return (
          <Suspense fallback={<PageLoader />}>
            <ClickToCallPage slug={slug} />
          </Suspense>
        );
      }
    }
  }

  if (isLoading) {

    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Check if this is an admin route - handle admin separately
  if (window.location.pathname.startsWith('/admin')) {
    return (
      <Suspense fallback={<PageLoader />}>
        <AdminApp />
      </Suspense>
    );
  }

  if (!isAuthenticated) {
    return (
      <Suspense fallback={<PageLoader />}>
        {/* HubSpot chat on the public order pages only (anonymous). */}
        <PublicOrderChat />
        <Switch>
          <Route path="/auth/login" component={Login} />
          <Route path="/auth/register" component={Register} />
          {/* Referral invite links: /signup?invite=<CODE> (Get free credits). */}
          <Route path="/signup" component={Register} />
          {/* The trial signup. Deep link: /free-signup?number=%2B1…&country=US
              &numberType=local[&plan=lite|starter|growth&billing=…]
              (lib/trialDeepLink). Was unrouted, so it fell through to the paid
              number picker below. */}
          <Route path="/free-signup" component={Register} />
          <Route path="/auth/google-callback" component={GoogleCallback} />
          <Route path="/activation" component={Activation} />
          <Route path="/verify-email" component={VerifyEmail} />
          <Route path="/auth/forgot-password" component={ForgotPassword} />
          <Route path="/auth/reset-password" component={ResetPassword} />
          <Route path="/mobile-login" component={MobileLogin} />
          <Route path="/mobile" component={MobileLogin} />
          <Route path="/mobile-inbox" component={MobileLogin} />
          <Route path="/pricing" component={PricingPage} />
          <Route path="/rates" component={RatesPage} />
          <Route path="/faq" component={FAQPage} />
          <Route path="/contact-sales" component={ContactSalesPage} />
          <Route path="/test-analytics" component={TestAnalytics} />
          <Route path="/help" component={HelpCenter} />
          <Route path="/support" component={HelpCenter} />
          <Route path="/api-docs" component={ApiDocs} />
          <Route path="/developers" component={ApiDocs} />
          <Route path="/demo/web-dialer" component={WebDialerDemo} />
          <Route path="/demo" component={DemoRequest} />
          <Route path="/demo/:token" component={DemoLanding} />
          {/* Entry point for limephone.io (Webflow) — carries the chosen number
              across the origin hop that sessionStorage can't survive. */}
          <Route path="/start" component={StartFromMarketingPage} />
          <Route path="/select-plan" component={SelectPlanPage} />
          <Route path="/kyc-address" component={KycAddressPage} />
          <Route path="/account-suspended" component={AccountSuspendedPage} />
          {/* Where queryClient sends an ACCOUNT_PENDING_REVIEW/REJECTED 403. The
              page reads its own state from /api/activation/status, so it does
              not need the org context that failed to load. */}
          <Route path="/pending-approval" component={PendingApprovalPage} />
          {/* Email deep-links carry their own session token (?token=), so the
              wizard must be reachable before the auth context hydrates. */}
          <Route path="/verify" component={VerifyNumberPage} />
          <Route path="/kyc-upload" component={KycUploadPage} />
          <Route path="/kyc-link-expired" component={KycLinkExpiredPage} />
          <Route path="/checkout" component={CheckoutPage} />
          <Route path="/checkout/success" component={CheckoutSuccessPage} />
          <Route path="/" component={Landing} />
          <Route path="/post-checkout" component={PostCheckoutPage} />
          <Route component={Landing} />
        </Switch>
      </Suspense>
    );
  }

  // Activation review gate: a brand-new org (activation_status = 'pending')
  // gets the pending page INSTEAD of the app — no route exists to deep-link
  // around it, and it re-appears on every login until an admin approves.
  // Onboarding and checkout stay reachable: the review happens at the END of
  // signup, not in the middle of it. The server enforces the teeth (outbound
  // voice, SMS, campaigns and API keys all 403); this is the explanation.
  //
  // ⚠⚠ 'rejected' holds here too. This test was `=== 'pending'`, which would
  // have dropped a REJECTED account into the full app — a decided-against
  // account seeing more of the product than one still waiting. NULL still
  // passes: 1,355 orgs predate the gate and are exempt.
  if (
    isAuthenticated &&
    (currentOrganization?.activationStatus === 'pending' ||
      currentOrganization?.activationStatus === 'rejected') &&
    !isOnboardingPage &&
    !location.startsWith('/checkout') &&
    !location.startsWith('/post-checkout') &&
    !location.startsWith('/verify-email')
  ) {
    return (
      <Suspense fallback={<PageLoader />}>
        <PendingApprovalPage />
      </Suspense>
    );
  }

  // Component that ensures capabilities are loaded for all authenticated routes
  function AuthenticatedRoutes() {
    // Fetch capabilities for all authenticated routes
    // This ensures capabilities are available for feature gating across the app
    useCapabilities();
    const { user } = useAuth();
    const { refreshWidget, identifyUser, hasLoaded } = useHubspotChat();
    const [location, setLocation] = useLocation();
    
    // Check for pending KYC that requires user attention
    useEffect(() => {
      const checkPendingKYC = async () => {
        // Only run when user is authenticated
        if (!user) return;
        const token = localStorage.getItem('token');
        if (!token) return;
        // Skip check for certain pages
        const skipPages = ['/kyc-status', '/kyc-upload', '/numberkyc', '/post-checkout', '/checkout', '/onboarding', '/admin', '/setup', '/verify-email', '/mobile'];
        if (skipPages.some(p => location.startsWith(p))) return;
        // /kyc-status just found nothing to show and sent them here. Don't send
        // them straight back: if the two endpoints disagree that is a loop.
        try {
          const emptyAt = Number(sessionStorage.getItem('kycStatusEmptyAt') || 0);
          if (Date.now() - emptyAt < 5 * 60 * 1000) return;
        } catch { /* private mode */ }

        try {
          const res = await fetch('/api/organization/pending-kyc-check', {
            headers: { 'Authorization': `Bearer ${token}` },
            credentials: 'include'
          });
          if (res.ok) {
            const data = await res.json();
            if (data.hasPendingKYC) {
              setLocation('/kyc-status');
            }
          }
        } catch (e) {
          // Silently fail - don't block app
        }
      };
      checkPendingKYC();
    }, [location, setLocation, user]);

    // Trial welcome call (server lib/welcomeCall): ask once per session, owner
    // only, for an approved trial. Fire-and-forget — the server decides
    // everything else (flag, number, once-per-org claim).
    const orgId = currentOrganization?.id;
    const welcomeEligible = !!user && currentOrganization?.lifecycle === 'trialing'
      && currentOrganization?.activationStatus === 'approved'
      && currentOrganization?.membership?.role === 'owner';
    useEffect(() => {
      if (!welcomeEligible || !orgId) return;
      const key = `welcomeCallAsked:${orgId}`;
      try {
        if (sessionStorage.getItem(key)) return;
        sessionStorage.setItem(key, '1');
      } catch { /* private mode: the server's once-per-org claim still holds */ }
      const token = localStorage.getItem('token');
      fetch('/api/onboarding/welcome-call', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: '{}',
      }).catch(() => { /* best-effort */ });
    }, [welcomeEligible, orgId]);

    // Force users back into post-checkout if they paid but didn't finish
    // (closed the tab on phone verification or use-case selection).
    // The endpoint only flags users with a pending paid order, so historical
    // users with active numbers are never redirected.
    useEffect(() => {
      const checkPostCheckoutRequired = async () => {
        if (!user) return;
        const token = localStorage.getItem('token');
        if (!token) return;
        const skipPages = ['/post-checkout', '/checkout', '/kyc-status', '/kyc-upload', '/admin', '/verify-email', '/onboarding', '/setup', '/auth/'];
        if (skipPages.some(p => location.startsWith(p))) return;

        try {
          const res = await fetch('/api/organization/post-checkout-required', {
            headers: { 'Authorization': `Bearer ${token}` },
            credentials: 'include',
          });
          if (res.ok) {
            const data = await res.json();
            if (data.required) {
              setLocation('/post-checkout');
            }
          }
        } catch (e) {
          // Silently fail — don't block the app on network errors
        }
      };
      checkPostCheckoutRequired();
    }, [location, setLocation, user]);
    
    // Pages where sidebar should NOT appear
    const hideSidebarPaths = [
      '/onboarding',
      '/kyc-upload',
      '/mobile',
      '/mobile-inbox',
      '/mobile-app',
      '/reactivate',
      '/verify-email',
      '/setup',
      '/pricing',
      '/rates',
      '/faq',
      '/contact-sales',
      '/help',
      '/support',
      '/api-docs',
      '/developers',
      '/plans',
      '/checkout',
      '/post-checkout',
    ];
    const showSidebar = !hideSidebarPaths.some(path => location.startsWith(path));
    
    const isMobilePage = location.startsWith('/mobile');

    // Refresh HubSpot widget on route changes for SPAs (skip on mobile)
    useEffect(() => {
      if (hasLoaded && !isMobilePage) {
        refreshWidget();
      }
    }, [location, hasLoaded, refreshWidget, isMobilePage]);

    // Identify user to HubSpot when loaded (skip on mobile)
    useEffect(() => {
      if (hasLoaded && user?.email && !isMobilePage) {
        const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
        identifyUser(user.email, fullName || undefined);
      }
    }, [hasLoaded, user, identifyUser, isMobilePage]);

    // Remove HubSpot widget on mobile pages
    useEffect(() => {
      if (isMobilePage && hasLoaded && window.HubSpotConversations?.widget) {
        window.HubSpotConversations.widget.remove();
      }
    }, [isMobilePage, hasLoaded]);

    // --- Mobile/tablet device auto-redirect ---
    // Redirect mobile/tablet users to /mobile (or /dashboard-selector if no preference saved)
    // Uses existing useDeviceDetection hook + DashboardSelector's localStorage preference
    const { isMobile: isMobileDevice, isTablet: isTabletDevice } = useDeviceDetection();
    const isSmallDevice = isMobileDevice || isTabletDevice;
    // True when served under a LimeCall-branded hostname (app.limecall.com).
    const onLimecallHost = isLimecallHost();
    // The LimeCall experience is walled off: a limecall-brand org (or a limecall
    // host) sees ONLY the /limecall dashboard, never the LimePhone app surface.
    const isLimecallExperience = currentOrganization?.product === 'limecall' || onLimecallHost;

    // Paths exempt from mobile redirect (already mobile-optimized or public/utility pages)
    const mobileRedirectExemptPaths = [
      '/mobile', '/mobile-login', '/mobile-inbox', '/mobile-app',
      '/dashboard-selector',
      '/auth/', '/pricing', '/rates', '/faq', '/contact-sales',
      '/help', '/support', '/api-docs', '/developers',
      '/kyc-upload', '/kyc-link-expired', '/kyc-status',
      '/checkout', '/post-checkout', '/select-plan', '/kyc-address', '/start',
      '/verify-email', '/onboarding', '/reactivate', '/admin', '/setup',
      '/plans', '/demo', '/limecall',
    ];
    const isExemptFromMobileRedirect = mobileRedirectExemptPaths.some(p => location.startsWith(p));

    useEffect(() => {
      // The LimeCall experience owns its own landing (redirect below).
      if (isLimecallExperience) return;
      if (!isSmallDevice || isExemptFromMobileRedirect) return;

      // Check if user already chose a dashboard preference
      const savedPref = localStorage.getItem('dashboardPreference');
      if (savedPref === '/mobile' || savedPref === '/mobile-app') {
        // User previously chose mobile — go straight there
        setLocation('/mobile');
      } else if (savedPref === '/dashboard' || savedPref === '/dashboard/personal') {
        // User explicitly chose desktop on a mobile device — let them stay (rare but respect it)
        return;
      } else {
        // No preference saved: phones go straight to the mobile dashboard
        // ("Full dashboard" in its name menu saves the opposite choice). Tablets
        // can run the full dashboard, so they get the chooser instead.
        setLocation(isMobileDevice && !isTabletDevice ? '/mobile' : '/dashboard-selector');
      }
    }, [isSmallDevice, isMobileDevice, isTabletDevice, isExemptFromMobileRedirect, setLocation]);

    // Wall off the LimeCall experience: a limecall-brand org (or limecall host)
    // is confined to /limecall — any other in-app route bounces back to it.
    // A few paths stay reachable (logout + billing/verification lifecycle) so
    // users aren't trapped. No-op for LimePhone orgs on the default host.
    useEffect(() => {
      if (!isLimecallExperience) return;
      const allowedPrefixes = ['/limecall', '/logout', '/checkout', '/post-checkout', '/reactivate', '/verify-email', '/kyc'];
      const allowed = allowedPrefixes.some((p) => location.startsWith(p));
      if (!allowed) setLocation('/limecall');
    }, [isLimecallExperience, location, setLocation]);

    return (
      <>
        {/* Main content wrapper - adds right padding when sidebar is visible */}
        <div className={`authenticated-app-shell min-h-screen bg-background text-foreground ${showSidebar && !isLimecallExperience ? "pr-14" : ""}`}>
          <Suspense fallback={<PageLoader />}>
            <Switch>
            <Route path="/verify-email" component={VerifyEmail} />
          <Route path="/reactivate" component={ReactivationPage} />
          <Route path="/" component={Welcome} />
          <Route path="/dashboard-selector" component={DashboardSelector} />
          <Route path="/dashboard" component={PersonalDashboard} />
          <Route path="/dashboard/personal" component={PersonalDashboard} />
          <Route path="/dashboard/company" component={CompanyDashboard} />
          <Route path="/limecall" component={LimeCallDashboard} />
          <Route path="/limecall/:tab" component={LimeCallDashboard} />
          <Route path="/demo" component={DemoRequest} />
          <Route path="/demo/:token" component={DemoLanding} />
          <Route path="/getting-started" component={GettingStarted} />
          <Route path="/setup" component={Setup} />
          <Route path="/inbox" component={InboxConversations} />
          <Route path="/inbox-enhanced" component={InboxRedesigned} />
          <Route path="/inbox-old" component={Inbox} />
          <Route path="/textinbox" component={TextInbox} />
          <Route path="/follow-ups" component={FollowUps} />
          <Route path="/helpdesk" component={Helpdesk} />
          <Route path="/status" component={StatusPage} />
          <Route path="/automations" component={Automations} />
          <Route path="/schedule" component={Schedule} />
          <Route path="/team-chat" component={TeamChat} />
          <Route path="/get-started" component={GetStarted} />
          <Route path="/search" component={SearchPage} />
          <Route path="/contacts" component={ContactsRedesigned} />
          <Route path="/contacts/:id" component={ContactsRedesigned} />
          <Route path="/customers" component={Customers} />
          <Route path="/campaigns" component={Campaigns} />
          <Route path="/campaigns-legacy" component={CampaignsLegacy} />
          <Route path="/templates" component={Templates} />
          <Route path="/analytics" component={Analytics} />
          <Route path="/workflows" component={Workflows} />
          <Route path="/voice" component={EnhancedVoicePage} />
          <Route path="/voice-dialer" component={VoiceDialerPage} />
          <Route path="/dialer" component={PowerDialerPage} />
          <Route path="/voice-agents" component={VoiceAgents} />
          <Route path="/voice-agents/new" component={VoiceAgentNew} />
          <Route path="/voice-calls" component={VoiceCalls} />
          {/* "AI call summary ready" notifications linked here before it pointed at /voice-calls. */}
          <Route path="/calls">{() => { window.location.replace("/voice-calls"); return null; }}</Route>
          <Route path="/inbound-routing" component={InboundRouting} />
          <Route path="/queue-management" component={QueueManagement} />
          <Route path="/call-records" component={CallRecords} />
          <Route path="/voicemails" component={Voicemails} />
          <Route path="/sms-setup" component={SmsSetup} />
          {/* An existing customer can reach the marketing site's "buy a number"
              CTA too. Without this they'd hit /start while authenticated and get
              a 404 — send them to the in-app number flow instead of the signup
              one, which is not built for an account that already exists. */}
          <Route path="/start" component={PhoneNumbers} />
          <Route path="/phone-numbers" component={PhoneNumbers} />
          <Route path="/phone-management-improved/:phoneNumberSid" component={PhoneManagementImproved} />
          <Route path="/phone-management/:phoneNumberSid" component={PhoneManagementImproved} />
          <Route path="/teams" component={Teams} />
          <Route path="/trust-center" component={TrustCenter} />
          <Route path="/kyc-status" component={KYCStatusPage} />
          <Route path="/verify" component={VerifyNumberPage} />
          <Route path="/numberkyc" component={NumberKycPage} />
          <Route path="/kyc-upload" component={KycUploadPage} />
          <Route path="/kyc-link-expired" component={KycLinkExpiredPage} />
          <Route path="/compliance" component={ComplianceDashboard} />
          {/* Retired: the old page posted to the dead 10dlc_registrations table. The
              real form lives in the Trust Center (organization_compliance). */}
          <Route path="/compliance/10dlc-registration">{() => { window.location.replace("/trust-center?tab=10dlc"); return null; }}</Route>
          <Route path="/compliance/10dlc-registration-enhanced" component={Enhanced10DLCRegistration} />
          <Route path="/onboarding" component={OnboardingPage} />
          {/* Signed in already: keep the link's number for the number step. */}
          <Route path="/free-signup">{() => {
            captureTrialLink(new URLSearchParams(window.location.search), browserStores());
            window.location.replace("/onboarding");
            return null;
          }}</Route>
          <Route path="/mobile" component={MobileApp} />
          <Route path="/mobile-inbox" component={MobileInboxLegacy} />
          <Route path="/mobile-app" component={MobileApp} />
          <Route path="/pricing" component={PricingPage} />
          <Route path="/rates" component={RatesPage} />
          <Route path="/faq" component={FAQPage} />
          <Route path="/contact-sales" component={ContactSalesPage} />
          <Route path="/test-analytics" component={TestAnalytics} />
          <Route path="/help" component={HelpCenter} />
          <Route path="/support" component={HelpCenter} />
          <Route path="/plans" component={Plans} />
          <Route path="/api-docs" component={ApiDocs} />
          <Route path="/developers" component={ApiDocs} />
          <Route path="/settings/integrations/slack/link" component={SlackLinkPage} />
          <Route path="/settings/integrations" component={SettingsNew} />
          <Route path="/settings/payments" component={PaymentsSettings} />
          <Route path="/billing/credits" component={BillingCredits} />
          <Route path="/free-credits">{() => <FreeCredits />}</Route>
          <Route path="/settings" component={SettingsNew} />
          <Route path="/checkout" component={CheckoutPage} />
          <Route path="/checkout/success" component={CheckoutSuccessPage} />
          <Route path="/checkout/claim/:orderId" component={CheckoutClaimPage} />
          <Route path="/settings/:tab" component={SettingsNew} />
          <Route path="/assistant" component={AssistantPage} />
          <Route path="/post-checkout" component={PostCheckoutPage} />
            <Route component={NotFound} />
            </Switch>
          </Suspense>
        </div>
        
        {/* Dashboard Right Sidebar - visible on all dashboard pages except excluded paths */}
        {showSidebar && !isLimecallExperience && <DashboardRightSidebar />}
        {showSidebar && <KeyboardShortcuts />}
      </>
    );
  }
  
  return (
    <>
      <AuthenticatedRoutes />
      {/* Global Call Button removed - dialer now available in DashboardRightSidebar */}
    </>
  );
}

// Wrapper component that handles page tracking inside providers
// ONLY loads analytics on signup and onboarding pages
function PageTrackingWrapper({ children }: { children: React.ReactNode }) {
  usePageTracking(); // Only tracks signup/onboarding pages
  return (
    <ConditionalAnalytics>
      {children}
    </ConditionalAnalytics>
  );
}

function AppWithProviders() {
  const [location] = useLocation();
  const isOnboardingPage = location.startsWith('/onboarding');
  const isVerifyEmailPage = location.startsWith('/verify-email');
  const isPublicPage = location.startsWith('/auth/') || location.startsWith('/mobile-login');
  const isAdminPage = location.startsWith('/admin');
  const { isAuthenticated } = useAuth();
  // On a LimeCall-branded host, suppress LimePhone billing/upgrade chrome — the
  // LimeCall dashboard has its own account/billing surface. (Org-level limecall
  // suppression is handled inside AuthenticatedRoutes, which has org context.)
  const onLimecallBrandHost = isLimecallHost();

  // Only show upgrade modal on dashboard pages (not on public/login/signup/onboarding/verify-email/admin)
  const shouldShowUpgradeModal = isAuthenticated && !isOnboardingPage && !isPublicPage && !isVerifyEmailPage && !isAdminPage && !onLimecallBrandHost;

  return (
    <OrganizationProvider>
      <NotificationsProvider>
        <UpgradeModalProvider>
        <DirectUpgradeProvider>
          <PageTrackingWrapper>
          {!isOnboardingPage && !isVerifyEmailPage ? (
            <MilestoneCelebrationProvider>
              <GlobalVoiceProvider>
                <GlobalDialerProvider>
                  <ProductTourProvider>
                    <Toaster />
                    {isAuthenticated && !onLimecallBrandHost && <PaymentFailureBanner />}
                    {/* The trial banner is hidden. The sidebar already carries a
                        trial card with the days remaining and an upgrade CTA, so
                        this was a second countdown saying the same thing in a
                        strip across the top of every page. The component is left
                        in place — restoring it is this one line. */}
                    {isAuthenticated && !onLimecallBrandHost && <FreePlanBanner />}
                    {isAuthenticated && !onLimecallBrandHost && <UsageLimitWarnings />}
                    {isAuthenticated && <GlobalLimitHandler />}
                    <Router />
                    <WelcomeTourModal />
                    <TourTrigger />
                    {shouldShowUpgradeModal && <GlobalUpgradeModal />}
                  </ProductTourProvider>
                </GlobalDialerProvider>
              </GlobalVoiceProvider>
            </MilestoneCelebrationProvider>
          ) : (
            <>
              <Toaster />
              <Router />
            </>
          )}
          </PageTrackingWrapper>
        </DirectUpgradeProvider>
        </UpgradeModalProvider>
      </NotificationsProvider>
    </OrganizationProvider>
  );
}

function AppWithTheme() {
  const { isAuthenticated } = useAuth();
  
  return (
    <ThemeProvider isAuthenticated={isAuthenticated}>
      <AppWithProviders />
    </ThemeProvider>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={({ error, resetError }) => <SentryFallback error={error} resetError={resetError} />}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <AppWithTheme />
        </TooltipProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
